import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext/ThemeContext';
import { Shield, Zap, ArrowRight, Users, Briefcase, Star, Quote } from 'lucide-react';

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('reveal-visible'); io.unobserve(e.target); } }),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const FEATURES = [
  { icon: <Shield className="w-5 h-5" />, title: 'Trusted Platform', body: 'Verified users and secure communication at every step.' },
  { icon: <Zap className="w-5 h-5" />, title: 'Flexible Work', body: 'Find part-time, full-time, or task-based work that fits your life.' },
  { icon: <Star className="w-5 h-5" />, title: 'Skill Recognition', body: 'Get recognized for your skills, not just your degree.' },
  { icon: <Users className="w-5 h-5" />, title: 'Direct Connection', body: 'Connect directly with clients or workers — no middlemen.' },
];



const TRUST_STATS = [
  { value: '10K+', label: 'Active Users', icon: <Users className="w-4 h-4" /> },
  { value: '5K+', label: 'Jobs Posted', icon: <Briefcase className="w-4 h-4" /> },
  { value: '4.8', label: 'User Rating', icon: <Star className="w-4 h-4" /> },
];

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const heroRef = useRef(null);
  useReveal();

  const goToWork = () => navigate(isAuthenticated ? '/worker' : '/login');
  const goToPost = () => navigate(isAuthenticated ? '/provider' : '/login');

  const bgImage = isDark ? '/images/team/dark.png' : '/images/team/light.avif';

  return (
    <div className="min-h-screen font-sans" style={{
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>

      {/* ── HERO: Large rounded container with background image matching the requested design ── */}
      <section ref={heroRef} className="relative pt-6 pb-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--hero-bg)' }}>
        <div className="max-w-7xl mx-auto rounded-[2rem] sm:rounded-[3rem] overflow-hidden relative shadow-2xl min-h-[85vh] flex items-center p-8 sm:p-16 border border-transparent highlight-border">
          
          {/* Background Image (Girl) */}
          <motion.img
            initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}
            src="/images/team/girl.png" alt="Professional woman working"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          
          {/* Dark Overlay for readability */}
          <div className="absolute inset-0 bg-black/40 sm:bg-black/20"></div>

          {/* Left Content (Text in Glass Boxes) */}
          <div className="relative z-10 w-full max-w-lg">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="glass-panel p-6 sm:p-8 rounded-[1.5rem] inline-block mb-6 max-w-[300px] sm:max-w-[360px]">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4"
                style={{ color: '#ffffff', lineHeight: '1.1' }}>
                Where Skills Meet <br/><span style={{ color: 'var(--accent-color)' }}>Opportunities</span>
              </h1>
              <p className="text-xs sm:text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                MattersUrSkill connects talented individuals with real work opportunities. Whether you're looking to hire or earn, we make it simple, trusted, and efficient.
              </p>
            </motion.div>

            <br/>

            {/* Buttons in a smaller glass panel */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
              className="glass-panel p-3 rounded-[1.25rem] flex flex-col sm:flex-row gap-3 inline-flex">
              <button onClick={goToWork}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105 active:scale-[0.98] shadow-md"
                style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Find Work <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={goToPost}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105 active:scale-[0.98]"
                style={{ background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}>
                Find Workers <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Stats shifted down and styled with Glassmorphism */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center gap-4 flex-wrap mt-8">
              {TRUST_STATS.map(({ value, label, icon }) => (
                <div key={label} className="flex items-center gap-3 px-5 py-3 rounded-2xl glass-panel">
                  <div style={{ color: 'var(--accent-color)' }}>{icon}</div>
                  <div>
                    <p className="text-lg font-bold leading-none" style={{ color: '#ffffff' }}>{value}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{label}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Floating cards on the right (hidden on very small screens) */}
          <div className="absolute top-16 right-12 hidden lg:flex flex-col gap-6">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.6 }}
              className="rounded-2xl p-5 w-56 glass-panel highlight-border">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: 'var(--accent)', color: 'var(--accent-color)' }}>
                <Users className="w-5 h-5" />
              </div>
              <p className="font-bold text-xs" style={{ color: '#ffffff' }}>Find the Right Opportunity</p>
              <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Explore jobs that match your skills.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.8 }}
              className="rounded-2xl p-5 w-56 glass-panel highlight-border ml-12">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: 'var(--accent)', color: 'var(--accent-color)' }}>
                <Briefcase className="w-5 h-5" />
              </div>
              <p className="font-bold text-xs" style={{ color: '#ffffff' }}>Hire the Best Talent</p>
              <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Find skilled and reliable people.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FEATURES bar (Light: white bg | Dark: transparent for gradient) ── */}
      <section className="py-16" style={{ background: 'transparent' }}>
        <div className="section-container-wide">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map(({ icon, title, body }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-start gap-4 p-5 rounded-2xl glass-panel highlight-border transition-all hover:scale-[1.02]">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {icon}
                </div>
                <div>
                  <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--foreground)' }}>{title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS (Light: transparent | Dark: transparent) ── */}
      <section className="py-24 section-glow" style={{ background: 'transparent' }}>
        <div className="section-container relative">
          
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: 'var(--foreground)' }}>Your journey to success</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-16 mt-8">
            {[
              { title: 'Create Your Profile', img: '/images/team/createprofile.png' },
              { title: 'Find or Post Work', img: '/images/team/findwork.png' },
              { title: 'Get Paid Safely', img: '/images/team/pay.png' },
            ].map(({ title, img }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.15 }}
                className="relative flex flex-col items-center text-center group">
                <div className="relative w-48 h-48 sm:w-64 sm:h-64 mb-4 transition-transform duration-500 group-hover:scale-105">
                  <img src={img} alt={title} className="w-full h-full object-contain drop-shadow-2xl" />
                  
                  {/* Floating Text Box */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 glass-panel highlight-border px-5 py-2.5 rounded-xl whitespace-nowrap shadow-lg">
                    <h3 className="text-sm font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>{title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section className="py-24 overflow-hidden section-glow" style={{ background: 'transparent' }}>
        <style>
          {`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-marquee {
              display: flex;
              width: max-content;
              animation: marquee 30s linear infinite;
            }
          `}
        </style>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 reveal">
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>Work for every skill</h2>
          </div>
          <div className="relative flex overflow-x-hidden py-8">
            <div className="animate-marquee gap-6 px-4">
              {[
                { label: 'AI Development', sub: 'Machine Learning', img: '/images/team/ai.jpg' },
                { label: 'Web Design', sub: 'Frontend & UI', img: '/images/team/web.jpg' },
                { label: 'Business Strategy', sub: 'Consulting', img: '/images/team/business.jpg' },
                { label: 'UI/UX Design', sub: 'Prototyping', img: '/images/team/uiux.avif' },
                { label: 'SEO Marketing', sub: 'Growth & Social', img: '/images/team/seo.png' },
                { label: 'Graphic Design', sub: 'Brand Identity', img: '/images/team/graphic.jpg' },
                { label: 'Social Media', sub: 'Content Creation', img: '/images/team/social.jpg' },
                { label: 'Repair Services', sub: 'Home & Office', img: '/images/team/repair.jpg' },
                { label: 'Cleaning', sub: 'Local Services', img: '/images/team/clean.jpg' },
                { label: 'AI Development', sub: 'Machine Learning', img: '/images/team/ai.jpg' },
                { label: 'Web Design', sub: 'Frontend & UI', img: '/images/team/web.jpg' },
                { label: 'Business Strategy', sub: 'Consulting', img: '/images/team/business.jpg' },
                { label: 'UI/UX Design', sub: 'Prototyping', img: '/images/team/uiux.avif' },
                { label: 'SEO Marketing', sub: 'Growth & Social', img: '/images/team/seo.png' },
                { label: 'Graphic Design', sub: 'Brand Identity', img: '/images/team/graphic.jpg' },
                { label: 'Social Media', sub: 'Content Creation', img: '/images/team/social.jpg' },
                { label: 'Repair Services', sub: 'Home & Office', img: '/images/team/repair.jpg' },
                { label: 'Cleaning', sub: 'Local Services', img: '/images/team/clean.jpg' }
              ].map(({ label, sub, img }, i) => (
                <a key={i} href={isAuthenticated ? '/worker' : '/login'}
                  className="group block rounded-2xl p-6 glass-panel highlight-border transition-transform duration-300 hover:-translate-y-2 hover:scale-105 flex-shrink-0 w-[280px]">
                  <img src={img} alt={label} className="w-full h-32 object-cover rounded-xl mb-4 shadow-sm" />
                  <h3 className="font-bold text-base mb-1" style={{ color: 'var(--foreground)' }}>{label}</h3>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{sub}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS (Light: white bg | Dark: black/zinc bg for separation) ── */}
      <section className="py-20" style={{ background: 'transparent' }}>
        <div className="section-container">
          <div className="text-center mb-12 reveal">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Testimonials</p>
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>What our users say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Priya Sharma', role: 'Freelance Developer', text: 'MattersUrSkills completely changed how I find work. Secure payments and verified clients give me real peace of mind.' },
              { name: 'Rahul Mehra', role: 'Small Business Owner', text: 'I found an amazing graphic designer within hours. The quality of talent on this platform is unmatched.' },
              { name: 'Anjali Singh', role: 'Content Specialist', text: 'The skill matching is spot on. I get paired with clients who need exactly what I offer. Highly recommend!' },
            ].map((review, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl p-8 border glass-panel highlight-border" style={{ borderColor: 'var(--border)' }}>
                <Quote className="w-6 h-6 mb-4" style={{ color: 'var(--accent-color)', opacity: 0.4 }} />
                <p className="text-xs leading-relaxed mb-6 italic" style={{ color: 'var(--foreground)' }}>"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                    style={{ background: 'var(--muted)', color: 'var(--foreground)' }}>
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-xs" style={{ color: 'var(--foreground)' }}>{review.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{review.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20" style={{ background: 'transparent' }}>
        <div className="section-container text-center">
          <motion.h2 initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>
            Ready to get started?
          </motion.h2>
          <p className="text-xs sm:text-sm mb-8 max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Join 10,000+ workers and 5,000+ businesses already using MattersUrSkills to connect, work, and grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={goToWork}
              className="px-8 py-3.5 rounded-xl font-semibold text-xs transition-all hover:opacity-90 active:scale-[0.98] border shadow-md"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
              Register as Worker
            </button>
            <button onClick={goToPost}
              className="px-8 py-3.5 rounded-xl font-semibold text-xs transition-all hover:bg-[var(--accent)] active:scale-[0.98] border"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
              Post a Job
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
